// Finding one notice or payment among a year's worth of them.
//
// A school sends a lot of these: a form's worth of letters, a term's worth of fees. After a few
// months the list is the wrong tool -- you know the thing exists and you want it, not a scroll.
//
// THE SCHOOL YEAR IS DERIVED, NOT STORED. Every notice already carries the moment it was issued and
// every payment the moment it was created, and the school already defines its years with real start
// and end dates. Reading the year off the date it happened means the filter works on every record
// ever written, with no field to add and no migration to run -- and it can never disagree with the
// date shown beside it, which a stored copy eventually would.

/** Case and surrounding space are never what somebody meant to search for. */
export const normalise = (s) => String(s ?? '').trim().toLowerCase();

/**
 * Which school year a date falls in, according to the years the school actually defined.
 *
 * No Sep-1 convention and no guessing: a school that runs August to July, or that has not set its
 * years up at all, gets the honest answer rather than a plausible-looking wrong one. A date outside
 * every defined year returns '' -- it belongs to no year, and pretending otherwise would file it
 * under one the school never created.
 */
export const schoolYearFor = (iso, years) => {
  const day = String(iso || '').slice(0, 10);
  if (!day) return '';
  const hit = (years || []).find(y => (
    y?.startDate && y?.endDate && day >= String(y.startDate) && day <= String(y.endDate)
  ));
  return hit ? String(hit.yearId || '') : '';
};

/**
 * Does any of these fields contain the term?
 *
 * Substring, not word-prefix: staff search for the middle of a title as often as the start of it
 * ("swimming gala" typed as "gala"), and a fee is remembered by its amount as much as its name.
 * Every space-separated word must appear somewhere, so "gala fee" finds a notice whose title and
 * description each hold one of them.
 */
export const matchesTerm = (parts, term) => {
  const words = normalise(term).split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = (parts || []).map(normalise).filter(Boolean).join(' ');
  return words.every(w => hay.includes(w));
};

/** The searchable text of a notice: what it is called, what it says, and who it is from. */
export const noticeText = (n) => [
  n?.title, n?.content, n?.issuerName, n?.issuerId, n?.issuedByName, n?.noticeId,
];

/**
 * The searchable text of a payment, including the amount as it is displayed.
 *
 * "180" has to find a $180 fee -- that is how a payment is remembered when its name has been
 * forgotten, and it is the one number a parent quotes on the phone.
 */
export const paymentText = (p) => [
  p?.title, p?.description, p?.createdByName, p?.payId,
  Number.isFinite(Number(p?.amountCents)) ? (Number(p.amountCents) / 100).toFixed(2) : '',
];

/**
 * Filter a list by school year and search term.
 *
 * `year` of '' means every year, including records that fall outside all of them -- so nothing can
 * hide from the default view. Choosing a specific year is the only way to narrow it.
 */
export const filterRecords = (list, { term = '', year = '', years = [], dateField, textOf }) => (
  (list || []).filter((r) => {
    if (year && schoolYearFor(r?.[dateField], years) !== year) return false;
    return matchesTerm(textOf(r), term);
  })
);

/**
 * Years worth offering, newest first, and only the ones the school defined.
 *
 * Read from academic_years rather than from the records: a year with nothing in it yet is still a
 * year somebody may be looking at, and a dropdown that grows as letters are sent is a dropdown that
 * moves under the person using it.
 */
export const yearOptions = (years) => (
  (years || [])
    .filter(y => y?.yearId)
    .slice()
    .sort((a, b) => String(b.yearId).localeCompare(String(a.yearId)))
);

/**
 * Which year to start on.
 *
 * The current one, but ONLY if it actually holds something. A filter that opens on an empty screen
 * reads as a broken page rather than as a filter, and the person then has to work out that there is
 * a dropdown to change before they see anything at all.
 */
export const initialYear = (years, lists) => {
  const current = (years || []).find(y => y?.cur_year);
  if (!current?.yearId) return '';
  const has = (lists || []).some(({ list, dateField }) => (
    (list || []).some(r => schoolYearFor(r?.[dateField], years) === current.yearId)
  ));
  return has ? String(current.yearId) : '';
};
